import { useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../common/Button";
import { bugsnagClient } from "../../../bugsnag";
import api from "../../../utils/api";
import { parseApiError } from "../../../utils/parseApiError";
import type { LoginFormData } from "./types";

export const Login = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
    setError("");
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const data = await api.login(formData);
      if (!data.token) {
        throw new Error(data.message || t("auth.login.failed"));
      }
      localStorage.setItem("token", data.token);
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      // If logging in via invite link, accept the invitation after login
      const params = new URLSearchParams(location.search);
      const tokenParam = params.get("token");
      if (tokenParam) {
        try {
          const result = await api.acceptInvite(tokenParam);
          if (result?.organization?.name) {
            sessionStorage.setItem(
              "org_welcome",
              `Welcome to ${result.organization.name}!`,
            );
          }
        } catch (inviteError) {
          // Non-blocking: token may be invalid or already used
          console.warn("Invite acceptance failed:", inviteError);
          if (inviteError instanceof Error) {
            bugsnagClient.notify(inviteError);
          } else {
            bugsnagClient.notify(new Error(String(inviteError)));
          }
        }
        window.location.href = "/organization";
      } else {
        window.location.href = "/";
      }
    } catch (loginError) {
      if (loginError instanceof Error) {
        bugsnagClient.notify(loginError);
      } else {
        bugsnagClient.notify(new Error(String(loginError)));
      }
      const { message } = parseApiError({ error: loginError });
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          src="/monco-logo-black.png"
          alt="Monco.ai"
          className="mx-auto h-12 w-auto"
        />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t("auth.login.title")}
        </h2>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                {t("auth.login.email_label")}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                {t("auth.login.password_label")}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                {t("auth.forgot.link")}
              </Link>
            </div>
            <div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={isLoading}
                className="w-full"
              >
                {isLoading
                  ? t("auth.login.submitting")
                  : t("auth.login.submit")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
